import { useEffect } from "react";
import Loading from "@/shared/components/Loading";
import { useUserSector } from "@/core/hooks/useUserSector";
import { useSectorAccessState } from "@/core/hooks/useSectorAccessState";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { RoutePaths } from "../constants";
import { useAuth } from "@/core/hooks/useAuth";
import {
  getOnboardingV2ResumePath,
  isOnboardingV2InProgress,
} from "@/features/onboarding-v2/utils/storage";

const SectorGate: React.FC = () => {
  const { sectorQuery, activeSectorId, isActiveSectorReady } = useUserSector();
  const { data: sector, isLoading, isError } = sectorQuery;
  const { user } = useAuth();
  const location = useLocation();
  const accessQuery = useSectorAccessState(activeSectorId);
  // Segura a tela só até a PRIMEIRA resposta do access-state. Depois de uma
  // falha, não bloqueia mais: refazer uma query com erro e sem dado volta a
  // `isLoading` no React Query v5, e cada página que monta abaixo do gate
  // refaz a query. Sem este corte, o gate desmontava a página, ela montava de
  // novo, refazia a query e o app ficava preso no Loading para sempre com o
  // access-state fora do ar. Plano desconhecido é tratado pelas páginas
  // (`PermissionsErrorState`), e o billing é fail-open.
  const isAccessLoading =
    accessQuery.isLoading && (accessQuery.errorUpdateCount ?? 0) === 0;

  // Só avisa o erro de um setor que é deste usuário. O setor guardado de outro
  // login (ou de um setor apagado) responde 403 antes de a lista de acessos
  // chegar, e o provider troca para um setor válido logo em seguida.
  const shouldWarnSectorError = isError && isActiveSectorReady;

  // O aviso fica num efeito, nunca no corpo do render: mexer na store das
  // notificações durante um render interrompível (o retry do Suspense quando o
  // MainLayout ainda carrega) reagendava o render, que avisava de novo, e o app
  // ficava preso no Loading com o toast na tela.
  useEffect(() => {
    if (!shouldWarnSectorError) return;
    notifications.hide("sector-error");
    notifications.show({
      id: "sector-error",
      color: "red",
      title: "Erro de conexão",
      message:
        "Não foi possível verificar o estado da sua conexão. Tente novamente.",
    });
  }, [shouldWarnSectorError]);

  if (isLoading || isAccessLoading) {
    return <Loading />;
  }

  const allowedDisconnectedPrefixes = [
    "/chats",
    "/team-management",
    "/agent",
    // Dashboard (métricas/Metabase) é acessível mesmo desconectado — admin
    // pode consultar indicadores históricos sem WhatsApp ativo.
    "/dashboard",
    // Contatos e follow-ups são acessíveis desconectado (coerente com o ícone
    // sempre habilitado): gerenciar contatos e configurar follow-ups não exige
    // WhatsApp ativo — o cron de follow-up só dispara quando conectado.
    "/contacts",
    "/follow-ups",
    // /settings/* deve ser acessível mesmo desconectado: aba whatsapp expõe
    // o DisconnectedCard para reconexão; outras abas (advanced, attendings)
    // não dependem de conexão ativa.
    "/settings",
    // Gate de billing (tela de planos / paywall) é ortogonal à conexão do
    // WhatsApp: um signup novo (modo "com cartão") ainda não tem WhatsApp
    // conectado e precisa cair AQUI. Sem isso o SectorGate redireciona
    // /ativar-plano → /chats e o SectorAccessGuard devolve /chats →
    // /ativar-plano, criando loop infinito.
    RoutePaths.PLAN_GATE,
    // Integrações não exige WhatsApp conectado: configurar/instalar integrações
    // (ex.: CRM, agenda) é ortogonal à conexão. Sem esta entrada o SectorGate
    // redireciona /integrations → /chats mesmo para admin, ao contrário das
    // demais abas do menu. startsWith cobre /integrations/:slug e .../install.
    RoutePaths.INTEGRATIONS,
    // Disparo em massa (SA-1170): a feature aparece sempre no menu e o histórico
    // é navegável mesmo desconectado — só a AÇÃO de disparar é travada, pelo
    // CampaignHealthGate/useCampaignDispatchGate (banner + CTA "Resolver
    // conexão"). Sem esta entrada o SectorGate bounce-ava /campaigns → /chats
    // antes da lista renderizar, anulando esse fluxo. startsWith cobre
    // /campaigns/new, /campaigns/:id e a revisão.
    RoutePaths.CAMPAIGNS,
  ];

  const isAllowedWhenDisconnected = allowedDisconnectedPrefixes.some(
    (prefix) =>
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );

  const onboardingResumePath = getOnboardingV2ResumePath(user?.documentId);
  const shouldResumeOnboarding = isOnboardingV2InProgress(user?.documentId);

  if (shouldResumeOnboarding && location.pathname !== RoutePaths.ONBOARDING) {
    return (
      <Navigate
        to={onboardingResumePath ?? RoutePaths.ONBOARDING}
        replace
      />
    );
  }

  if (!isAllowedWhenDisconnected && sector?.session_state !== "connected") {
    return (
      <Navigate
        to="/chats"
        replace
      />
    );
  }

  return <Outlet />;
};

export default SectorGate;
